/**
 * 5897 — Qualidade do modelo: erro por antecedência (do snapshot), como o
 * modelo foi montado, as variáveis que ele usa e como foi validado.
 */
import { FC } from 'react'
import styled from 'styled-components'
import { BD, Card, PageSubtitle, PageTitle, Table } from '../styles'
import { BarraReferencia, CartaoTabela, ComPainel, Entenda, Kpi, Kpis, Rolagem, pct } from './comum'
import { GraficoLinhas, pctFmt } from './graficos'

const LINHAS_ERRO: Array<[string, 'curto' | 'longo']> = [
  ['120', 'longo'],
  ['90', 'longo'],
  ['60', 'longo'],
  ['45', 'longo'],
  ['30', 'curto'],
  ['14', 'curto'],
  ['7', 'curto'],
  ['3', 'curto'],
  ['1', 'curto'],
]

const erro = (o: any, metodo: string): string => (o?.[metodo]?.MdAPE !== undefined ? pct(o[metodo].MdAPE) : '—')

/** variável, o que é */
const VARIAVEIS_EVENTO: Array<[string, string]> = [
  ['Dias até o evento', 'Quanto falta para o evento. Define também qual dos dois modelos é usado (até 30 dias ou de 31 a 120).'],
  ['Pagos até ontem', 'Ingressos pagos acumulados até o dia anterior (cortesias e cancelados fora).'],
  ['Ritmo de 3, 7 e 14 dias', 'Pagos vendidos nos últimos 3, 7 e 14 dias.'],
  ['Aceleração', 'Ritmo dos últimos 3 dias comparado ao dos últimos 7: diz se a venda está acelerando ou freando.'],
  ['Dias em venda', 'Há quantos dias o evento teve a primeira venda.'],
  ['Curva do estilo', 'Quanto do total o evento típico do mesmo estilo já tinha vendido neste mesmo ponto (2022+).'],
  ['Curva do produtor', 'Quanto do total os eventos anteriores do mesmo produtor já tinham vendido neste ponto (com ≥3 eventos).'],
  ['Histórico do produtor', 'Quantos eventos o produtor já realizou na BD.'],
  ['Fatia do site', 'Parte dos pagos que veio do site (o resto é PDV e comissário).'],
  ['Dia da semana, mês e hora', 'Quando o evento acontece.'],
  ['Estilo e região', 'Estilo cadastrado e região do país (N, NE, CO, SE, S).'],
]
const VARIAVEIS_LOTE: Array<[string, string]> = [
  ['Tamanho e saldo', 'Quantidade do lote e quanto ainda falta vender.'],
  ['Fração vendida', 'Vendidos ÷ quantidade.'],
  ['Ritmo do lote', 'Vendas por dia nos últimos 3 e 7 dias, e a aceleração entre os dois.'],
  ['Saldo ÷ ritmo', 'Quantos dias o saldo duraria no ritmo atual, comparado aos dias até o evento.'],
  ['Dias até o evento e idade do lote', 'Quanto falta para o evento e há quantos dias o lote vende.'],
  ['Número do lote e preço', 'Se é o 1º, 2º, 3º lote… e o preço à vista.'],
  ['Venda do evento', 'Pagos acumulados do evento e da última semana.'],
  ['Estilo', 'Estilo cadastrado do evento.'],
]

const Passos = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: passo;
  display: grid;
  gap: 12px;
  li {
    counter-increment: passo;
    display: grid;
    grid-template-columns: 28px 1fr;
    gap: 10px;
    font-size: 14px;
    line-height: 1.5;
  }
  li::before {
    content: counter(passo);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${BD.black};
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    display: grid;
    place-items: center;
  }
  strong {
    display: block;
  }
  span {
    color: ${BD.grayText};
  }
`

const Secao = styled.h2`
  font-size: 18px;
  font-weight: 800;
  margin: 32px 0 6px;
`

const Grade2 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 440px), 1fr));
  gap: 16px;
  margin-bottom: 16px;
`

const TabelaSimples: FC<{ cabecalho: string[]; linhas: string[][]; numericas?: number[] }> = ({
  cabecalho,
  linhas,
  numericas = [],
}) => (
  <Rolagem>
    <Table>
      <thead>
        <tr>
          {cabecalho.map((c, i) => (
            <th key={c} className={numericas.indexOf(i) >= 0 ? 'n' : undefined}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {linhas.map((l) => (
          <tr key={l[0]} style={{ cursor: 'default' }}>
            {l.map((v, i) => (
              <td key={i} className={numericas.indexOf(i) >= 0 ? 'n' : undefined} style={i === 0 ? { fontWeight: 500 } : undefined}>
                {v}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  </Rolagem>
)

export const PrevisoesQualidade: FC = () => (
  <ComPainel>
    {(painel, referencias, estudo) => {
      const { curto, longo, gmv } = painel.qualidade
      const { ALERTAS, ANTECEDENCIAS, CALIBRACAO_LOTE, DATA_ESTUDO, ERRO_POR_METODO, ESTABILIDADE, JANELAS_K, LOTE_BRIER } = estudo
      return (
        <>
          <PageTitle>Qualidade do modelo</PageTitle>
          <PageSubtitle>
            Como a previsão foi montada, o que ela usa e quanto erra em eventos que ela nunca viu (teste de jul/2025 a
            out/2026).
          </PageSubtitle>
          <BarraReferencia painel={painel} referencias={referencias} />
          <Kpis>
            <Kpi
              rotulo="Projeção por evento · D-7"
              valor={erro(curto['7'], 'modelo GBM (mediana)')}
              dica={`erro mediano · conta de cabeça: ${erro(curto['7'], 'ritmo 7d')}`}
            />
            <Kpi rotulo="Projeção por evento · D-1" valor={erro(curto['1'], 'modelo GBM (mediana)')} dica="erro mediano" />
            <Kpi
              rotulo="Faixa de 80%"
              valor={curto['7'] ? pct(curto['7'].cobertura_80) : '—'}
              dica="dos eventos dentro da faixa (D-7)"
            />
            <Kpi
              rotulo="GMV eventos abertos · 30 dias"
              valor={gmv['30']?.evento_so_abertos ? pct(gmv['30'].evento_so_abertos.MdAPE) : '—'}
              dica="erro mediano por data do evento"
            />
            <Kpi
              rotulo="GMV por data da venda · 7 dias"
              valor={painel.gmv['7']?.venda_erro_tipico ? pct(painel.gmv['7'].venda_erro_tipico as number) : '—'}
              dica="erro mediano fora da amostra (eventos + ritmo recente)"
            />
          </Kpis>
          <Entenda
            paragrafos={[
              'GMV por data da venda: em 26/10/2026 a soma das projeções por evento previu R$ 507 mil para um dia de R$ 260 mil. Reavaliamos os métodos fora da amostra (45 origens semanais de jul/2025 a jul/2026, viés e faixa sempre calibrados só com as semanas anteriores): até 14 dias a combinação da soma por eventos com o ritmo dos últimos 7 dias é a mais estável (erro típico de 30% a 1 dia, 24% a 3, 23% a 7 e 14); de 30 dias em diante a soma por eventos vence com folga (27–36%, contra 43–58% do ritmo recente). As faixas de 80% passaram a vir desse teste fora da amostra, não mais do ajuste em cima dos mesmos dados.',
              'Simulador de evento: modelo de árvores ex-ante (produtor, local, cidade, estilo, data, preço, estrutura e artistas), validado em 2.929 eventos de jul/2025 a out/2026 simulados só com o que existia antes da abertura: erro mediano de 32% quando o produtor já fez evento no mesmo local, 46% com histórico em outro local e 54% para produtor novo (38% no total; a mediana dos comparáveis, que era o método anterior, errava 51%).',
            ]}
          />

          <Secao>Como o modelo foi montado</Secao>
          <Card style={{ marginBottom: 16 }}>
            <Passos>
              <li>
                <div>
                  <strong>Dados</strong>
                  <span>
                    Vendas contadas pelos ingressos (não pelo contador do cadastro, que diverge), com a data da compra
                    original nas transferências. Eventos de 2019 e de 2022 em diante; 2020 e 2021 (pandemia) ficam fora
                    porque tiveram janelas de venda curtas e atípicas, e incluí-los piorou o teste. Contas de teste e
                    treinamento ficam fora.
                  </span>
                </div>
              </li>
              <li>
                <div>
                  <strong>Fotografias do passado</strong>
                  <span>
                    Para cada evento realizado, reconstruímos o que se sabia em cada dia antes dele (D-120, D-90… D-1): só
                    as vendas até aquele dia. A pergunta que o modelo aprende é "quanto ainda vai vender daqui até o
                    evento", e não o total direto, o que deixa eventos grandes e pequenos na mesma escala.
                  </span>
                </div>
              </li>
              <li>
                <div>
                  <strong>Dois modelos de árvores (gradient boosting)</strong>
                  <span>
                    Um para D-30 a D-1, mais preciso, e outro para D-120 a D-31. Cada um combina centenas de pequenas
                    árvores de decisão que aprendem com as variáveis abaixo. A projeção mostrada é a mediana (P50).
                  </span>
                </div>
              </li>
              <li>
                <div>
                  <strong>Faixa de 80% calibrada</strong>
                  <span>
                    A faixa P10–P90 não é chute: ela é medida nos erros reais dos últimos 6 meses antes do teste
                    (calibração conforme), para que o resultado caia dentro dela em cerca de 8 de cada 10 eventos.
                  </span>
                </div>
              </li>
              <li>
                <div>
                  <strong>Validação no futuro</strong>
                  <span>
                    O modelo foi treinado com eventos até jun/2025 e testado em {(3425).toLocaleString('pt-BR')} eventos de
                    jul/2025 a out/2026. O mesmo evento nunca aparece no treino e no teste. Comparamos com regras simples
                    (conta de cabeça) e repetimos em três janelas de um ano.
                  </span>
                </div>
              </li>
              <li>
                <div>
                  <strong>Em produção</strong>
                  <span>
                    Os modelos usados aqui foram re-treinados com todo o histórico validado. O cálculo roda toda
                    madrugada com as vendas até o dia anterior e grava a previsão do dia, para medir o erro ao vivo depois.
                    Re-treino recomendado: mensal.
                  </span>
                </div>
              </li>
            </Passos>
          </Card>

          <Grade2>
            <CartaoTabela>
              <div className="titulo">
                <h3>Variáveis da projeção de público</h3>
              </div>
              <TabelaSimples cabecalho={['Variável', 'O que é']} linhas={VARIAVEIS_EVENTO} />
            </CartaoTabela>
            <CartaoTabela>
              <div className="titulo">
                <h3>Variáveis do esgotamento de lote</h3>
                <div style={{ fontSize: 12.5, color: BD.grayText }}>
                  Três modelos: chance de esgotar em 7 dias, chance de esgotar antes do evento (só ingresso) e dias
                  até esgotar (janela provável).
                </div>
              </div>
              <TabelaSimples cabecalho={['Variável', 'O que é']} linhas={VARIAVEIS_LOTE} />
            </CartaoTabela>
          </Grade2>
          <Entenda
            paragrafos={[
              'O que ficou de fora, e por quê: artista (o banco não tem cadastro; pelo texto, só 22% dos eventos têm o artista identificado, e o histórico do artista não melhorou a previsão de forma consistente), Wikipedia e sinais digitais (sem ganho consistente em três janelas), feriados (sem efeito: 1,05×) e concorrência na mesma cidade e data (sem efeito: 1,04×).',
              'O banco não guarda capacidade do local nem campanhas de divulgação, então o modelo não sabe de ocupação nem de anúncios. Ele também não conhece o estoque restante: se o último lote esgotar, a venda bate no teto.',
            ]}
          />

          <Secao>Como chegamos a esses números</Secao>
          <PageSubtitle>Resultados do estudo de inteligência de vendas (dados até {DATA_ESTUDO}).</PageSubtitle>
          <Grade2>
            <GraficoLinhas
              titulo="Erro mediano da projeção, por antecedência"
              sub="Eventos de jul/2025 a out/2026 · menor é melhor"
              xs={ANTECEDENCIAS}
              series={ERRO_POR_METODO}
              invertido
              ymax={0.55}
              yticks={[0, 0.1, 0.2, 0.3, 0.4, 0.5]}
              xticks={[120, 90, 60, 45, 30, 14, 7, 1]}
              fmtX={(t) => `D-${t}`}
              tituloDica={(i) => `Previsão em D-${ANTECEDENCIAS[i]}`}
              entenda={[
                'O eixo horizontal é quando a previsão foi feita (D-120 = 120 dias antes; D-1 = véspera). O vertical é o erro típico do total de pagos: 20% num evento de 1.000 pagos quer dizer errar uns 200 para mais ou para menos.',
                '"Ritmo 7 dias" é a conta de cabeça (o ritmo da última semana continua igual até o evento). "Curva do produtor" usa quanto os eventos anteriores do produtor tinham vendido neste ponto. "Comparáveis" faz a conta com eventos parecidos (só até D-30). O modelo combina tudo isso com aceleração e estilo.',
                'O modelo é o melhor em todos os pontos: uma semana antes erra 17,6%, contra ~30% da conta de cabeça; a 120 dias erra 32%, contra 43%. Longe do evento a faixa cobre só 74–77% e o modelo tende a superestimar ~10%: por isso aparece como confiança baixa.',
              ]}
            />
            <GraficoLinhas
              titulo="O modelo se mantém em outros anos?"
              sub="Erro mediano em três janelas de teste de um ano"
              xs={JANELAS_K}
              series={ESTABILIDADE}
              invertido
              ymax={0.35}
              yticks={[0, 0.1, 0.2, 0.3]}
              xticks={JANELAS_K}
              fmtX={(t) => `D-${t}`}
              tituloDica={(i) => `Previsão em D-${JANELAS_K[i]}`}
              altura={260}
              entenda={[
                'Cada linha é uma janela de teste de um ano diferente; em cada uma, o modelo foi treinado só com o que existia antes dela.',
                'O erro fica parecido nos três anos: entre 18% e 20% uma semana antes e 10–11% na véspera. O modelo não acertou por sorte num período.',
                'Mudanças grandes no mercado (pandemia, migração para o site) podem piorar o modelo; por isso o erro é acompanhado ao vivo e o re-treino é mensal.',
              ]}
            />
          </Grade2>

          <CartaoTabela style={{ marginBottom: 16 }}>
            <div className="titulo">
              <h3>Erro por antecedência (modelos em uso)</h3>
            </div>
            <Rolagem>
              <Table>
                <thead>
                  <tr>
                    <th>Antecedência</th>
                    <th className="n">Erro mediano · modelo</th>
                    <th className="n">Conta de cabeça (ritmo 7 dias)</th>
                    <th className="n">Cobertura da faixa 80%</th>
                    <th>Modelo</th>
                  </tr>
                </thead>
                <tbody>
                  {LINHAS_ERRO.map(([k, qual]) => {
                    const o = (qual === 'curto' ? curto : longo)[k]
                    if (!o) return null
                    return (
                      <tr key={k} style={{ cursor: 'default' }}>
                        <td>D-{k}</td>
                        <td className="n">
                          <strong>{erro(o, 'modelo GBM (mediana)')}</strong>
                        </td>
                        <td className="n">{erro(o, 'ritmo 7d')}</td>
                        <td className="n">{pct(o.cobertura_80)}</td>
                        <td>{qual === 'curto' ? 'D-30 a D-1' : 'D-120 a D-31'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </Rolagem>
          </CartaoTabela>

          <Secao>Esgotamento de lote e alertas</Secao>
          <Grade2>
            <GraficoLinhas
              titulo="A probabilidade de esgotar acerta?"
              sub="Esgota antes do evento: prevista × frequência real (jul/2025+) · na diagonal = calibrado"
              xs={[0, 1]}
              series={CALIBRACAO_LOTE}
              pontos
              ymax={1}
              yticks={[0, 0.25, 0.5, 0.75, 1]}
              xticks={[0, 0.25, 0.5, 0.75, 1]}
              fmtX={pctFmt}
              tituloDica={(i) => `Faixa de probabilidade ${i + 1} de 6`}
              altura={300}
              entenda={[
                'Cada ponto junta lotes com probabilidade prevista parecida (horizontal) e mostra quantos esgotaram de fato (vertical). Na diagonal, a probabilidade está certa: de 100 lotes com 70%, cerca de 70 esgotam.',
                'O modelo fica perto da diagonal. A regra simples saldo ÷ ritmo fica longe: quando diz quase 100%, só metade esgota.',
                'Só entram lotes com quantidade definida no ingresso, desde 2024. Para estoque compartilhado de setor, "antes do evento" ainda não está validado.',
              ]}
            />
            <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
              <CartaoTabela>
                <div className="titulo">
                  <h3>Esgotamento de lote</h3>
                  <div style={{ fontSize: 12.5, color: BD.grayText }}>Brier: erro de uma probabilidade, menor é melhor.</div>
                </div>
                <TabelaSimples
                  cabecalho={['Pergunta', 'Taxa-base', 'Regra simples', 'Modelo']}
                  linhas={LOTE_BRIER}
                  numericas={[1, 2, 3]}
                />
              </CartaoTabela>
              <PageSubtitle style={{ margin: 0 }}>
                A janela provável de esgotamento acertou 83% dos casos (~5 dias de largura quando o fim está a até 3
                dias). "Sem evidência de esgotamento antes do evento" é um resultado válido e apareceu em 48% das
                previsões.
              </PageSubtitle>
            </div>
          </Grade2>
          <CartaoTabela>
            <div className="titulo">
              <h3>Alertas</h3>
              <div style={{ fontSize: 12.5, color: BD.grayText }}>
                Acerto = o evento terminou ao menos 20% longe da projeção feita antes do alerta.
              </div>
            </div>
            <TabelaSimples
              cabecalho={['Alerta', 'Eventos alertados', 'Acerto', 'Vezes a taxa-base', 'Decisão']}
              linhas={ALERTAS}
              numericas={[1, 2, 3]}
            />
          </CartaoTabela>
        </>
      )
    }}
  </ComPainel>
)
